import { useEffect, useMemo } from "react"
import { UseFormReturn, useForm } from "react-hook-form"

import { useAssignRef } from "shared/lib/useAssignRef"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  FloorFormData,
  FloorFormDataSchema
} from "features/floor-management/types/FloorFormData"
import { FloorFormOptions } from "features/floor-management/types/FloorFormOptions"

import { FloorFormProps } from "../FloorForm"
import { FloorFormAttachmentsContextType } from "../context/FloorFormAttachmentsContext"

export interface FloorFormViewModel extends FloorFormOptions {
  form: UseFormReturn<FloorFormData>
  onSubmit: () => void
  attachmentsContext: FloorFormAttachmentsContextType
}

export function useFloorFormViewModel({
  defaultFormData,
  onSubmit,
  onChange,
  formRef,
  isReadonly,
  attachments,
  linkedAttachments,
  attachmentCategories,
  onUpload,
  ...rest
}: FloorFormProps): FloorFormViewModel {
  const defaultAttachmentLinks = useMemo(() => {
    return Object.fromEntries(
      linkedAttachments?.map(attachment => [attachment.id, true]) ?? []
    )
  }, [linkedAttachments])

  const form = useForm<FloorFormData>({
    defaultValues: { ...defaultFormData, attachments: defaultAttachmentLinks },
    resolver: zodResolver(FloorFormDataSchema)
  })

  useAssignRef({ ref: formRef, value: form })

  useEffect(() => {
    if (!onChange) return undefined
    return form.subscribe({
      callback: ({ values }) => {
        onChange?.(values)
      },
      formState: { values: true }
    })
  }, [form, onChange])

  useEffect(() => {
    if (isReadonly) {
      form.reset({ ...defaultFormData, attachments: defaultAttachmentLinks })
    }
  }, [isReadonly])

  const handleSubmit = useMemo(() => {
    return form.handleSubmit(formData => {
      onSubmit?.(formData)
    })
  }, [form, onSubmit])

  useEffect(() => {
    form.reset(
      { ...defaultFormData, attachments: defaultAttachmentLinks },
      { keepDirty: true, keepDirtyValues: true }
    )
  }, [defaultFormData, defaultAttachmentLinks])

  const attachmentsContext = useMemo<FloorFormAttachmentsContextType>(
    () => ({
      attachments: attachments ?? null,
      attachmentCategories: attachmentCategories ?? null,
      linkedAttachments: linkedAttachments ?? null,
      onUpload
    }),
    [attachments, attachmentCategories, linkedAttachments, onUpload]
  )

  return {
    form,
    onSubmit: handleSubmit,
    attachmentsContext,
    ...rest
  }
}
